import { Button, useDisclosure } from '@nextui-org/react';
import Image from 'next/image';
import React from 'react';
import CurrencyInput from 'react-currency-input-field';

import { AIRI, WalletIcon } from '@/assets';
import { SelectBase } from '@/components';
import RenderWhen from '@/components/RenderWhen';
import useConnectErc20Wallet, { ConnectorNames } from '@/hooks/useConnectErc20Wallet';
import LoginModal from '@/layouts/header/Login';
import { centerTextEllipsis, formatCurrency } from '@/utils';

import { LOCK_OPTIONS, NETWORK_OPTIONS, NetworkKey } from '../constants';
import { useLockContext } from '../LockContext';
import { getEarnValues } from './helpers';
import useInfoLock from './useInfoLock';
import useLockWithBnbChain from './useLockWithBnbChain';
import useLockWithOraiChain from './useLockWithOraiChain';

const ActionLock = () => {
  const {
    amount,
    setAmount,
    option,
    setOption,
    isShowBalance,
    isLoginClient,
    user,
    validate,
    accountBsc,
    allowance,
  } = useInfoLock();
  const { apr, credit } = getEarnValues(Number(amount), option);
  const { refresh, network, setNetwork } = useLockContext();
  const { onSubmit: onSubmitBsc, loading: loadingBsc } = useLockWithBnbChain();
  const { handleLockToken, loading: loadingOrai } = useLockWithOraiChain(refresh);
  const { isOpen, onOpenChange, onOpen, onClose } = useDisclosure();
  const { connectErc20Wallet } = useConnectErc20Wallet();

  return (
    <div className="flex h-full flex-col justify-between rounded-[12px] bg-white px-8 py-6 shadow-[0px_8px_25px_5px_rgba(199,205,231,0.25)] sm:px-6 sm:py-4">
      <div>
        <div className="flex flex-wrap items-center justify-between">
          <div className="w-[155px]">
            <SelectBase
              classTrigger="h-[43px]"
              classTextTrigger="!text-black"
              classIcon="w-[24px] h-auto"
              onChange={(val) => setNetwork(val)}
              // value not label
              defaultValue={NETWORK_OPTIONS[0]?.value}
              list={NETWORK_OPTIONS}
            />
          </div>
          <RenderWhen>
            <RenderWhen.If isTrue={isShowBalance}>
              <div className="flex flex-wrap items-center sm:mt-3">
                <Image
                  className="h-auto w-[36px] lg:w-[32px] sm:w-[28px]"
                  src={WalletIcon}
                  alt="wallet"
                />
                <span className="ml-3 text-[26px] font-semibold lg:text-[22px] sm:ml-2 sm:text-[18px]">
                  {formatCurrency(user.airiBalance, 3)}
                </span>
                <Image
                  className="ml-2 h-auto w-[30px] lg:w-[26px] sm:w-[22px]"
                  src={AIRI}
                  alt="airi"
                />
                <span className="ml-3 text-gray-500 sm:ml-0 sm:w-full sm:text-[14px]">
                  ~${formatCurrency(user.airiToUsd, 3)}
                </span>
              </div>
            </RenderWhen.If>
          </RenderWhen>
        </div>
        <RenderWhen>
          <RenderWhen.If isTrue={isLoginClient}>
            <div className="mt-4 sm:mt-3">
              <div className="mb-2 flex items-center sm:mb-1">
                <p className="w-[110px] text-[18px] font-semibold lg:text-[16px] sm:w-[90px] sm:text-[14px]">
                  Username
                </p>
                <span className="sm:text-[14px]">{user.username}</span>
              </div>
              <div className="flex items-center">
                <p className="w-[110px] text-[18px] font-semibold lg:text-[16px] sm:w-[90px] sm:text-[14px]">
                  Wallet
                </p>
                <span className="sm:text-[14px]">{centerTextEllipsis(user.address, 8)}</span>
              </div>
            </div>
          </RenderWhen.If>
        </RenderWhen>
        <div className="mt-6 flex flex-wrap gap-[40px] sm:mt-4 sm:gap-5">
          <div className="w-[calc(50%-20px)] sm:w-full">
            <p className="mb-2 text-[18px] font-semibold lg:text-[16px] sm:text-[14px]">You lock</p>
            <div className="relative w-full">
              <CurrencyInput
                id="amount"
                name="amount"
                placeholder="0"
                value={amount}
                className="h-[45px] w-full rounded-[100px] bg-gray-50 px-4 pr-[60px] text-[15px] text-black outline-none sm:h-[40px] sm:text-[13px]"
                decimalsLimit={3}
                onValueChange={(value) => setAmount(value ?? '')}
              />
              <span className="absolute right-4 top-[50%] translate-y-[-50%] text-[14px] sm:text-[12px]">
                AIRI
              </span>
            </div>
          </div>
          <div className="w-[calc(50%-20px)] sm:w-full">
            <p className="mb-2 text-[18px] font-semibold lg:text-[16px] sm:text-[14px]">Within</p>
            <SelectBase
              classTrigger="h-[45px] md:h-[40px]"
              classTextTrigger="!text-black"
              onChange={(val) => setOption(val)}
              // value not label!!!
              defaultValue={LOCK_OPTIONS[0]?.value}
              list={LOCK_OPTIONS}
            />
          </div>
        </div>
        <div className="mt-5">
          <p className="mb-2 text-[18px] font-semibold lg:text-[16px] sm:text-[14px]">
            You’ll earn
          </p>
          <div className="grid grid-cols-3 gap-4 sm:grid-cols-1 sm:gap-2">
            <div className="rounded-[6px] border-1 border-gray-100 bg-gray-50 p-4 sm:py-2">
              <p className="mb-1 line-clamp-1 font-semibold text-blue-500 sm:mb-0 sm:text-[14px]">
                {apr}%
              </p>
              <span className="text-[14px] text-blue-500 sm:text-[12px]">APR</span>
            </div>
            {/* <div className="rounded-[6px] border-1 border-gray-100 bg-gray-50 p-4 sm:py-2">
              <p className="mb-1 line-clamp-1 font-semibold text-blue-500 sm:mb-0 sm:text-[14px]">
                {formatCurrency(aig, 3)}
              </p>
              <span className="text-[14px] text-blue-500 sm:text-[12px]">AIG</span>
            </div> */}
            <div className="rounded-[6px] border-1 border-gray-100 bg-gray-50 p-4 sm:py-2">
              <p className="mb-1 line-clamp-1 font-semibold text-blue-500 sm:mb-0 sm:text-[14px]">
                {formatCurrency(credit, 3)}
              </p>
              <span className="text-[14px] text-blue-500 sm:text-[12px]">Credits</span>
            </div>
          </div>
        </div>
      </div>
      <RenderWhen>
        <RenderWhen.If isTrue={!isLoginClient}>
          <Button
            onClick={onOpen}
            className="mt-6 h-[44px] w-full rounded-[100px] bg-orange-500 font-semibold text-white sm:h-[40px] sm:text-[13px]"
          >
            Login to continue
          </Button>
        </RenderWhen.If>
        <RenderWhen.If isTrue={!accountBsc && network === NetworkKey.Bsc && isLoginClient}>
          <Button
            onClick={() => connectErc20Wallet(ConnectorNames.MetaMask)}
            className="mt-6 h-[44px] w-full rounded-[100px] bg-orange-500 font-semibold text-white sm:h-[40px] sm:text-[13px]"
          >
            Login metamask
          </Button>
        </RenderWhen.If>
        <RenderWhen.If
          isTrue={!validate && (network === NetworkKey.Oraichain ? isLoginClient : !!accountBsc)}
        >
          <Button
            disabled
            isLoading={loadingBsc || loadingOrai}
            onClick={() =>
              network === NetworkKey.Bsc
                ? onSubmitBsc({
                    account: accountBsc,
                    amount: amount.toString(),
                    poolId: Number(option),
                    userId: user.id ? Number(user.id) : 0,
                    allowance,
                    callback: refresh,
                  })
                : handleLockToken({
                    poolId: Number(option),
                    amount: amount.toString(),
                    userId: user.id ? Number(user.id) : 0,
                    fees: 0,
                    gas: 400000,
                  })
            }
            className="mt-6 h-[44px] w-full rounded-[100px] bg-orange-500 font-semibold text-white opacity-50 sm:h-[40px] sm:text-[13px]"
          >
            Lock to earn
          </Button>
        </RenderWhen.If>
        <RenderWhen.If
          isTrue={
            !!validate && isLoginClient && (network === NetworkKey.Oraichain ? true : !!accountBsc)
          }
        >
          <Button
            disabled
            className="mt-6 h-[44px] w-full rounded-[100px] font-semibold text-white sm:h-[40px] sm:text-[13px]"
          >
            {validate}
          </Button>
        </RenderWhen.If>
      </RenderWhen>
      <LoginModal isOpen={isOpen} onOpenChange={onOpenChange} onClose={onClose} />
    </div>
  );
};

export default ActionLock;
